// `AnchorSentinels` (SuiteImport): marks ranges (comments, links, highlights) inside prose while it's being built, so
// they survive serialisation to the Markdown dialect and can be measured afterwards in Unicode scalars. A marker is a
// private-use scalar (U+E000 start, U+E001 end) followed by one encoding the anchor's index (plane 15). Symbol fonts do
// put U+E000/U+E001 in real text; without an index scalar after them they're kept as text.

const START = 0xe000;
const END = 0xe001;
const INDEX_BASE = 0xf0000;

export const AnchorSentinels = {
  start: String.fromCodePoint(START),
  end: String.fromCodePoint(END),

  marker(which: 'start' | 'end', index: number): string {
    return String.fromCodePoint(which === 'start' ? START : END) + String.fromCodePoint(INDEX_BASE + index);
  },

  /** Source text with anything that would read as a marker defused: an index scalar right after U+E000/U+E001 is
   *  dropped (the symbol-font glyph stays), so the document can't forge an anchor. */
  neutralise(text: string): string {
    if (!text.includes(AnchorSentinels.start) && !text.includes(AnchorSentinels.end)) return text;
    let out = '';
    let previous = -1;
    for (const ch of text) {
      const cp = ch.codePointAt(0)!;
      if (cp >= INDEX_BASE && (previous === START || previous === END)) continue;
      out += ch;
      previous = cp;
    }
    return out;
  },

  /** Removes markers from `markdown`, returning each anchor's scalar range. With `closeOpen`, a range left open runs to
   *  the end and an end without a start runs from the beginning; otherwise both are dropped. Empty ranges are dropped. */
  extract(markdown: string, closeOpen = false): { text: string; anchors: { index: number; range: [number, number] }[] } {
    const scalars = Array.from(markdown);
    const starts = new Map<number, number>();
    const anchors: { index: number; range: [number, number] }[] = [];
    let out = '';
    let position = 0;
    for (let i = 0; i < scalars.length;) {
      const cp = scalars[i].codePointAt(0)!;
      i += 1;
      if ((cp === START || cp === END) && i < scalars.length && scalars[i].codePointAt(0)! >= INDEX_BASE) {
        const index = scalars[i].codePointAt(0)! - INDEX_BASE;
        i += 1;
        if (cp === START) {
          starts.set(index, position);
        } else if (starts.has(index)) {
          const from = starts.get(index)!;
          starts.delete(index);
          if (position > from) anchors.push({ index, range: [from, position] });
        } else if (closeOpen && position > 0) {
          anchors.push({ index, range: [0, position] });
        }
        continue;
      }
      out += scalars[i - 1];
      position += 1;
    }
    if (closeOpen) for (const [index, from] of starts) if (position > from) anchors.push({ index, range: [from, position] });
    anchors.sort((a, b) => a.range[0] - b.range[0]);
    return { text: out, anchors };
  },
};
